import type { components } from '../../../api/generated/schema'
import type { CharacterSections, CharacterSheet, CoreSection } from '../types'

type Schema = components['schemas']

type StressSection = Schema['StressSectionDto']
type StressTrack = Schema['StressTrackDto']
type ConsequencesSection = Schema['ConsequencesSectionDto']
type AbilitiesSection = Schema['AbilitiesSectionDto']
type Ability = Schema['AbilityDto']
type EquipmentSection = Schema['EquipmentSectionDto']
type Equipment = Schema['EquipmentDto']
type Shape = Schema['ShapeDto']

/**
 * The printed sheet's presentation layer — arrangement only.
 *
 * Nothing here derives a game value. Every number on the printed page arrives already
 * computed from the server (architecture §4.4), exactly as it does for the screen sheet;
 * these functions decide what sits next to what on a sheet of A4 and in which order, which
 * is the one thing the API has no opinion about.
 *
 * It is kept apart from the components so the layout rules that matter — which variant of
 * the sheet a character gets, which powers belong to which shape, what the Gear block
 * collects — are testable without rendering a page.
 */

/**
 * The Fate ladder, printed as a legend down the side of the sheet the way the official
 * sheets print it. Static because it is the system's, not the character's: the server ships
 * a skill's rung as both its name and its number, and this is only the reader's key to them.
 */
export const LADDER: ReadonlyArray<{ value: number; label: string }> = [
  { value: 8, label: 'Legendary' },
  { value: 7, label: 'Epic' },
  { value: 6, label: 'Fantastic' },
  { value: 5, label: 'Superb' },
  { value: 4, label: 'Great' },
  { value: 3, label: 'Good' },
  { value: 2, label: 'Fair' },
  { value: 1, label: 'Average' },
  { value: 0, label: 'Mediocre' },
  { value: -1, label: 'Poor' },
  { value: -2, label: 'Terrible' },
]

/** `+4`, `+0`, `−2` — with a real minus sign, because this is print. */
export function signed(value: number): string {
  return value < 0 ? `−${Math.abs(value)}` : `+${value}`
}

/**
 * Which of the four printed layouts a character gets.
 *
 * This is the print equivalent of picking one of the four official sheets, and it is decided
 * from the same three facts the screen sheet reads: more than one shape, a Hunger track, and
 * the server's spellcasting flag. A shapeshifting vampire is a shapeshifter *and* keeps its
 * Hunger track — the blocks compose, so unlike the paper sheets nothing has to be dropped.
 */
export type SheetVariant = 'mortal' | 'spellcaster' | 'shapeshifter' | 'vampire'

export function sheetVariant(
  core: CoreSection | undefined,
  shapes: Shape[],
  stress: StressSection | undefined,
): SheetVariant {
  if (shapes.length > 1) return 'shapeshifter'
  if (hungerTrack(stress) !== undefined) return 'vampire'
  if (core?.hasSpellcasting === true) return 'spellcaster'
  return 'mortal'
}

const VARIANT_TITLES: Record<SheetVariant, string> = {
  mortal: 'Character Sheet',
  spellcaster: 'Character & Spellcasting Sheet',
  shapeshifter: 'Shapeshifter Sheet',
  vampire: 'Character & Hunger Sheet',
}

export function variantTitle(variant: SheetVariant): string {
  return VARIANT_TITLES[variant]
}

// ---------------------------------------------------------------------------
// Stress
// ---------------------------------------------------------------------------

export function isHungerTrack(track: StressTrack): boolean {
  return String(track.type) === 'Hunger'
}

/**
 * The three mundane tracks, in the order the official sheet prints them rather than the
 * order the API happens to return them. A track the character does not have simply does not
 * appear — the server decides which tracks exist.
 */
const MUNDANE_ORDER = ['Physical', 'Mental', 'Social']

export function mundaneTracks(stress: StressSection | undefined): StressTrack[] {
  return (stress?.tracks ?? [])
    .filter((track) => !isHungerTrack(track))
    .sort((a, b) => MUNDANE_ORDER.indexOf(String(a.type)) - MUNDANE_ORDER.indexOf(String(b.type)))
}

/**
 * The Hunger track, when the character has one. It prints in its own block rather than
 * beside the mundane three: it is not cleared between scenes, and only feeding empties it,
 * so a reader who mistakes it for a fourth stress track marks it off at the wrong time.
 */
export function hungerTrack(stress: StressSection | undefined): StressTrack | undefined {
  return (stress?.tracks ?? []).find(isHungerTrack)
}

/** Consequence slots, heaviest last — the order the paper sheet reads top to bottom. */
const CONSEQUENCE_ORDER = ['Mild', 'Moderate', 'Severe', 'Extreme']

export function consequenceSlots(
  consequences: ConsequencesSection | undefined,
): NonNullable<ConsequencesSection['slots']> {
  return [...(consequences?.slots ?? [])].sort(
    (a, b) =>
      CONSEQUENCE_ORDER.indexOf(String(a.level)) - CONSEQUENCE_ORDER.indexOf(String(b.level)),
  )
}

// ---------------------------------------------------------------------------
// Abilities
// ---------------------------------------------------------------------------

/**
 * Powers and stunts for one shape, split the way the sheet prints them.
 *
 * The shape filter is the screen sheet's, restated because it is scoping rather than
 * derivation: an ability carries the shapes it applies to, and one carrying none applies to
 * every shape. Getting it wrong prints a wolf's claws on the human sheet.
 */
export function abilitiesForShape(
  abilities: AbilitiesSection | undefined,
  shapeId: number | null,
): { powers: Ability[]; stunts: Ability[] } {
  const all = (abilities?.abilities ?? []).filter((ability) => {
    const ids = ability.shapeIds ?? []
    return ids.length === 0 || shapeId === null || ids.includes(shapeId)
  })

  const byName = (a: Ability, b: Ability) => (a.name ?? '').localeCompare(b.name ?? '')

  return {
    powers: all.filter((ability) => ability.type === 'Power').sort(byName),
    stunts: all.filter((ability) => ability.type === 'Stunt').sort(byName),
  }
}

// ---------------------------------------------------------------------------
// Gear
// ---------------------------------------------------------------------------

export interface GearGroup {
  label: string
  items: Equipment[]
}

/**
 * Mundane equipment, grouped for the Gear block.
 *
 * The official sheets have no equipment section at all — a wizard's revolver lives in a
 * one-line "Armor, Etc:" field, and everything else goes on the back of the page in
 * someone's handwriting. The app tracks weapons and armour properly, so the printed sheet
 * gives them a block: grouped by kind, equipped first inside each group, because what is in
 * your hands right now is what you reach for mid-scene.
 */
export function gearGroups(equipment: EquipmentSection | undefined): GearGroup[] {
  const items = equipment?.items ?? []
  if (items.length === 0) return []

  const order = ['Weapon', 'Armor']
  const labels: Record<string, string> = { Weapon: 'Weapons', Armor: 'Armour' }

  const groups = new Map<string, Equipment[]>()
  for (const item of items) {
    const kind = String(item.equipmentType ?? 'Other')
    const bucket = groups.get(kind)
    if (bucket === undefined) groups.set(kind, [item])
    else bucket.push(item)
  }

  return [...groups.entries()]
    .sort(([a], [b]) => rank(order, a) - rank(order, b))
    .map(([kind, group]) => ({
      label: labels[kind] ?? kind,
      items: group.sort((a, b) => {
        if (a.isEquipped !== b.isEquipped) return a.isEquipped === true ? -1 : 1
        return (a.name ?? '').localeCompare(b.name ?? '')
      }),
    }))
}

function rank(order: string[], value: string): number {
  const index = order.indexOf(value)
  return index === -1 ? order.length : index
}

/** `Weapon 2 · Ballistic · equipped` — the tags line under a gear entry. */
export function gearTags(item: Equipment): string[] {
  const tags: string[] = []

  const power = item.power ?? 0
  if (power !== 0) tags.push(`${String(item.equipmentType ?? 'Rating')} ${power}`)

  // `None` is the subtype a weapon that is simply a weapon carries; printing the word
  // would put "None" on the sheet beside every plain item.
  const subtype = item.equipmentSubtype
  if (subtype !== undefined && subtype !== 'None') tags.push(String(subtype))

  if (item.isEquipped === true) tags.push('equipped')
  return tags
}

// ---------------------------------------------------------------------------
// Fixed capacities
// ---------------------------------------------------------------------------

/**
 * How many rows each list block prints, whatever the character has in it.
 *
 * The sheet is a *form*, not a report: a block is a fixed box with a fixed number of ruled
 * lines, the character's entries fill it from the top, and what is left over is where the
 * next few sessions get written in pencil. That is what keeps the page count fixed — one
 * sheet for a character, two for a spellcaster — and it is why these are constants rather
 * than a function of how much the character happens to own today.
 *
 * A character who has already outgrown a box keeps every entry: the block grows and the
 * sheet runs long rather than silently dropping a power. The capacities are set well above
 * what a starting character carries so that stays the rare case it should be.
 */
export const CAPACITY = {
  otherAspects: 8,
  abilities: 13,
  /** A shapeshifter prints two skill blocks, so the powers box gives up rows to make room. */
  abilitiesWithTwoShapes: 5,
  gear: 7,
  /** Two skill boxes leave the gear box less room too. */
  gearWithTwoShapes: 5,
  focusItems: 8,
  enchantedItems: 8,
  roteSpells: 9,
  specializations: 6,
  /**
   * Blank consequence rows below the ones the character has. The official sheet prints four
   * spare circles between Severe and Extreme for the same reason: consequence slots are
   * something powers grant, so a character gains them mid-campaign.
   */
  spareConsequences: 2,
} as const

/**
 * Pads a list out to a fixed number of rows, `null` marking a blank one.
 *
 * The blanks are the point — they are the ruled lines a player writes the next stunt on.
 */
export function padRows<T>(items: readonly T[], capacity: number): Array<T | null> {
  const rows: Array<T | null> = [...items]
  while (rows.length < capacity) rows.push(null)
  return rows
}

// ---------------------------------------------------------------------------
// The stress track
// ---------------------------------------------------------------------------

/**
 * How many boxes a printed stress track shows, regardless of how many the character has.
 *
 * The official sheet prints eight. Ten is the ceiling this app's tracks can reach, and a
 * printed sheet outlives the session it came off the printer in: a character whose Endurance
 * goes up mid-campaign should be able to pencil in the box they just earned rather than
 * print the sheet again.
 */
export const STRESS_TRACK_LENGTH = 10

export interface StressCircle {
  value: number
  /** Whether the character actually has this box yet. Beyond their track, it prints hollow. */
  owned: boolean
  /** Whether it is filled right now, per the server. */
  marked: boolean
}

/**
 * One printed stress row: ten circles, of which the character owns the first few.
 *
 * A marked box gets a dot rather than a cross. The cross belongs to the player and their
 * pencil — that is what the circles are for — and pre-printing one would leave them nothing
 * to do but rub it out. The dot says "this is where the app thought you were when this came
 * off the printer", which is a different statement and reads as one.
 */
export function stressCircles(track: StressTrack | undefined): StressCircle[] {
  const boxes = track?.boxes ?? []

  return Array.from({ length: STRESS_TRACK_LENGTH }, (_, index) => {
    const box = boxes[index]
    return {
      value: index + 1,
      owned: box !== undefined,
      marked: box?.isStressed === true,
    }
  })
}

/**
 * The skill each track is derived from, which the official sheet prints under its name.
 *
 * It is a fact about the system rather than about the character — Physical stress comes off
 * Endurance for everyone — so it is a constant here and not something the API is asked for.
 * Hunger is the exception that proves it: it is not derived from a skill at all, and the
 * paper sheet still labels it Discipline because that is what a vampire rolls to resist it.
 */
const TRACK_SKILL: Record<string, string> = {
  Physical: 'Endurance',
  Mental: 'Conviction',
  Social: 'Presence',
  Hunger: 'Discipline',
}

export function trackSkill(type: string): string | undefined {
  return TRACK_SKILL[type]
}

// ---------------------------------------------------------------------------
// The blank form
// ---------------------------------------------------------------------------

/**
 * The top rung a blank sheet prints, whatever the character's own cap is.
 *
 * Every official blank sheet runs Superb (+5) down to Average (+1), because that is the
 * pyramid a Submerged game is built on and the shape everyone recognises. A cap is one of the
 * things a blank sheet is blanking, so it cannot come from the character.
 */
const BLANK_TOP_RUNG = 5

/** Ten circles a blank track prints — all of them the character's to claim. */
const BLANK_BOXES = Array.from({ length: STRESS_TRACK_LENGTH }, (_, index) => ({
  id: -(index + 1),
  value: index + 1,
  isStressed: false,
}))

/**
 * The same sheet with nothing written on it.
 *
 * Every box, rung, row and circle survives; every value the character put in one does not.
 * That is the distinction the option is offering — a table that wants a fresh sheet for a
 * new character, or a spare to write this session's changes onto, wants *this* character's
 * form (two pages if they cast, a Hunger row if they feed, two skill boxes if they change
 * shape) with the ink left out.
 *
 * It blanks the **data** rather than asking each box to render itself differently, so there
 * is one place to look for what a blank sheet does and no second rendering path to keep in
 * step with the first. The few numbers that survive are the ones that belong to the form
 * rather than to the character: a consequence's level and its penalty, the ladder, the
 * headings.
 */
export function blankSections(sections: Partial<CharacterSections>): Partial<CharacterSections> {
  const blank: Partial<CharacterSections> = { ...sections }

  if (sections.aspects !== undefined) {
    blank.aspects = {
      ...sections.aspects,
      highConcept: null,
      trouble: null,
      aspect1: null,
      aspect2: null,
      aspect3: null,
      aspect4: null,
      aspect5: null,
      aspect6: null,
      tempAspect1: null,
      tempAspect2: null,
      tempAspect3: null,
    }
  }

  // Omitted rather than zeroed: the sheet prints nothing for a figure it does not have, and
  // a printed `0` reads as a character with no skill points rather than as a blank line.
  blank.skills = { skills: [], maxLevelValue: BLANK_TOP_RUNG, maxSkillLevel: 'Superb' }
  delete blank.fate

  if (sections.stress !== undefined) {
    // The track types survive — a vampire's blank sheet still has a Hunger row — but every
    // circle comes back, unmarked and undashed: a blank form knows of no track length.
    blank.stress = {
      tracks: (sections.stress.tracks ?? []).map((track) => ({ ...track, boxes: BLANK_BOXES })),
    }
  }

  if (sections.consequences !== undefined) {
    blank.consequences = {
      slots: (sections.consequences.slots ?? []).map((slot) => ({
        ...slot,
        isOccupied: false,
        consequence: null,
      })),
    }
  }

  blank.abilities = { abilities: [] }
  blank.equipment = { items: [] }
  blank.specializations = { specializations: [] }
  blank.focusItems = { items: [] }
  blank.enchantedItems = { items: [] }
  blank.roteSpells = { spells: [] }

  return blank
}

/**
 * A figure for the sheet, or nothing at all.
 *
 * Never a `0` standing in for "not known" — on a form the two say different things, and the
 * blank sheet depends on the difference.
 */
export function figure(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value)
}

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/**
 * One shape's half of a shapeshifter's sheet.
 *
 * Skills, stress, consequences and the shape's own powers are all shape-scoped, so a
 * shapeshifter needs one of these per shape. The paper sheet has room for exactly two and
 * calls them Human and Changed; this prints one block per shape and lets the page flow, so
 * a character with four shapes loses nothing.
 */
export interface ShapeSheet {
  shape: Shape
  sheet: CharacterSheet
}

/** Shapes in the order the character defined them. */
export function orderedShapes(shapes: Shape[]): Shape[] {
  return [...shapes].sort((a, b) => (a.shapeIndex ?? 0) - (b.shapeIndex ?? 0))
}

/**
 * How many shapes one printed character sheet carries.
 *
 * The official Shapeshifter sheet prints two skill blocks side by side — Human and Changed —
 * and this follows it, because two columns is what a sheet of A4 holds without the powers
 * box shrinking to nothing. A character with more shapes prints their first two; the rest
 * stay on screen, where there is no page to run out of.
 */
export const MAX_PRINTED_SHAPES = 2

/** The shapes this sheet prints: the character's first two, in their own order. */
export function printedShapes(shapes: Shape[]): Shape[] {
  return orderedShapes(shapes).slice(0, MAX_PRINTED_SHAPES)
}

/**
 * The shapes a printed sheet needs to fetch *in addition to* the one already loaded.
 *
 * The sheet read is scoped to a single shape, so printing a shapeshifter means one read per
 * shape. The one already in hand is not re-requested, and shapes past the printed two are
 * never requested at all — fetching a sheet nothing will render is a round trip for nothing.
 */
export function otherShapeIds(shapes: Shape[], loadedShapeId: number | null): number[] {
  return printedShapes(shapes)
    .map((shape) => shape.id)
    .filter((id): id is number => id !== undefined && id !== loadedShapeId)
}

/** The name to print over a shape's block. Falls back to its index when it has none. */
export function shapeName(shape: Shape): string {
  const name = (shape.name ?? '').trim()
  return name === '' ? `Shape ${(shape.shapeIndex ?? 0) + 1}` : name
}
